import { EmbeddingModel, IngestStatus, LLMProvider, PdfReadMethod, LLMModel, AmbiguityLLMModel, MigrationStatus} from "./types"

export interface Tag {
    rowid: string
    tag_index: number
    value: string
    is_active: boolean
    description?: string
    updated_at?: Date
}

export interface TagValuesListResponse {
    count?: number
    has_next?: boolean
    tag_values?: Tag[]
}

export interface TagConfig {
    tag_label: string
    tag_index: string | number
    data_type?: string
    match_method?: string
    is_custom?: boolean
    is_active?: boolean
    is_mandatory?: boolean
    is_multiselect?: boolean
    updated_at?: Date
    protected_values?: string[]
    is_required?: boolean
}

export interface DocumentTag {
    document_id: string
    tag_value_id: string
}

export interface IngestFile {
    type: any
    document_name: string
    document_type: string
    created_at: Date
    created_by: string
    status: string
    id: string
    tags: Tag[]
    offline_ready?: boolean
}

export interface ListFilesResponse {
    count: number
    has_next: boolean
    documents: IngestFile[]
}

export interface DocumentsRequest {
    document_ids?: string[]
    document_names?: string[]
    reset_existing_status?: boolean,
    requested_by_email: string
}

export interface IngestPayload {
    document_ids?: Array<string>,
    rerun_failed: boolean,
    requested_by_email: string,
    skip_cache?: boolean,
}

export interface TenantConfig {
    enable_cache: boolean,
    preprocess_prompt: boolean,
    provider: LLMProvider,
    embedding_model: EmbeddingModel,
    enable_language: boolean,
    gpt_model: LLMModel,
    pdf_read_method: PdfReadMethod,
    enable_reranker: boolean,
    enable_detect_error_code_intent: boolean,
    enable_ambiguity: boolean,
    ambiguity_gpt_model: AmbiguityLLMModel,
    part_image_min_score?: number | null
}

export const DEFAULT_PART_IMAGE_MIN_SCORE = 0.35

export const DEFAULT_TENANT_CONFIG: TenantConfig = {
    enable_cache: false,
    preprocess_prompt: false,
    provider: 'Azure OpenAI',
    embedding_model: 'text-embedding-3-large',
    enable_language: false,
    gpt_model: 'gpt-4o',
    pdf_read_method: 'msr',
    enable_reranker: true,
    enable_detect_error_code_intent: false,
    enable_ambiguity: false,
    ambiguity_gpt_model: 'gpt-4o-mini',
    part_image_min_score: DEFAULT_PART_IMAGE_MIN_SCORE
}

export interface CachedItem {
    id: string,
    text: string,
    question: string
}

export interface IngestModel {
    model_id: string,
    model_name: string,
    status: IngestStatus,
    total_files?: number,
    pending?: number,
    failed?: number,
    processing?: number,
    ingested?: number
}

export interface CustomPromptAsk {
    rowid: string,
    prompt_scope: string,
    tenant_id: string,
    tag_id: string, // tenant_model
    prompt_title: string,
    prompt: string,
    created_at: Date,
    updated_at: Date
}
export interface AdapterTenantConfig {
    config_id: string,
    config_name: string,
    tenant_id: string,
    tenant_name: string
}

export interface SystemPromptResponse {
    success: boolean,
    result: string[]
}

// Update to clarify backend vs frontend model types
// Backend API models matching the Python BaseModel classes
// Base interface for shared suggestion fields
export interface BaseSuggestionModel {
  suggestion: string;
  rowid?: string;
}

export interface ObservationSuggestionModel extends BaseSuggestionModel {
  observation: string;
}

export interface HighlightSuggestionModel extends BaseSuggestionModel {
  chat_answer: string;
  highlight: string;
}

export interface UpdateSuggestionModel {
  rowid: string;
  suggestion: string;
}

export interface HighlightSuggestionUpdateRequest {
  update: UpdateSuggestionModel[];
};

// Frontend models with additional ID field for DataGrid
export interface ObservationSuggestion extends ObservationSuggestionModel {
  id: string;
}

export interface HighlightSuggestion extends HighlightSuggestionModel {
  id: string;
}

// Generic paginated response interface
export interface PaginatedResponse<T> {
  data: T[];
  total_count: number;
  total_pages: number;
}

// Replace specific paginated responses with type aliases
export type ObservationSuggestionsResponse = PaginatedResponse<ObservationSuggestionModel> & { enabled?: boolean };
export type HighlightSuggestionsResponse = PaginatedResponse<HighlightSuggestionModel>;

export interface ObservationsSuggestionsUpsertResponse {
  added: ObservationSuggestionModel[];
  updated: ObservationSuggestionModel[];
}

export interface HighlightSuggestionsUpsertResponse {
  updated: HighlightSuggestionModel[];
}

export interface UpdateHighlightSuggestionsResponse {
  updated: UpdateSuggestionModel[];
}

export interface GeneralResponse {
  status: string;
  message: string;
}

export interface ConfigMigrationStatus {
  status: MigrationStatus;
  details: {
    main_db: {
      config: {
        pdf_read_method: string;
        embedding_model: string;
      };
      documents: {
        uploaded: number;
        queued: number;
        processing: number;
        done: number;
        failed: number;
        not_migrated: number | null;
        not_migrated_ids: string[] | null;
      };
    };
    migration_db: {
      config: {
        pdf_read_method: string;
        embedding_model: string;
      };
      documents: {
        uploaded: number;
        queued: number;
        processing: number;
        done: number;
        failed: number;
        not_migrated: number;
        not_migrated_ids: string[];
      };
    };
  };
}

export interface PrepareDocumentsResponse {
  documents: {
    document_id: string;
    progress: string;
  }[];
}

export interface TagValueGroup {
    group_id: string | null
    tag_value_ids: string[]
    tag_values?: string[]
    member_count?: number
    created_at?: string
}

export interface TagValueGroupsListResponse {
    groups: TagValueGroup[]
}

export interface TagValueGroupUpsertItem {
    // Absent / null → backend creates a new group with a generated UUID.
    // Present + existing → that group's contents are replaced.
    group_id?: string | null
    tag_value_ids: string[]
}

export interface TagValueGroupsUpsertResponse {
    success: boolean
    groups_created: number
    groups_updated: number
    invalid_tag_value_ids?: string[]
    invalid_group_ids?: string[]
}

export interface TagValueGroupsDeleteResponse {
    success: boolean
    deleted: number
}
export interface PartImageIngestResponse {
    tenant_id: string
    group_id: string
    count: number
}

// Only the fields the SID admin flows need - the backend's Part payload is much wider.
export interface PartListItem {
    id: string
    images: { rowid: string }[]
}

export interface ListPartsResponse {
    count: number
    total_count: number
    has_next: boolean
    parts: PartListItem[]
}

export interface DeletePartsResponse {
    success: boolean
    deleted_count: number
}
